import React, { useContext, useEffect } from 'react';
import type {
  SchemaNumber,
  SchemaNumberWithUnit,
  UnitLengthPercentage,
  SchemaString,
  SchemaBoolean,
  EffectNineDirections,
  EffectSpinDirections,
  EffectTwoAxes,
  Shape,
} from '@wix/effects-kit';
import {
  Divider,
  Composites,
  TextLabel,
  Slider,
  TextInput,
  DropDown,
  DropDownOption,
  AngleInput,
  DropDownIllustrationOption,
  Checkbox,
  Thumbnails,
} from '@wix/wix-base-ui';
import { TranslationContext } from '../context/TranslationContext';
import { CustomizeControlProps } from '../types';
import {
  ArrowDown,
  ArrowUpDown,
  Reset,
  Corners,
} from '@wix/wix-ui-icons-common/classic-editor';
import styles from '../assets/styles.scss';
import Shapes from '../assets/thumbs/shapes';

const prettyUnits = (units: string[]) =>
  units.map((unit) => (unit === 'percentage' ? '%' : unit));

const displayDataTransformer = {
  default: [(x: any) => x, (x: any) => x],
  duration: [(ms: number) => ms / 1000, (s: number) => s * 1000],
  delay: [(ms: number) => ms / 1000, (s: number) => s * 1000],
};

const getDisplayTransformer = (name: string) =>
  name in displayDataTransformer
    ? displayDataTransformer[name as keyof typeof displayDataTransformer]
    : displayDataTransformer.default;

type DirectionProps = {
  angle: number;
  flip?: boolean;
  icon: React.FC<any>;
};

const directions: Record<
  EffectNineDirections | EffectSpinDirections | EffectTwoAxes,
  DirectionProps
> = {
  'top-left': { angle: 315, icon: ArrowDown },
  top: { angle: 0, icon: ArrowDown },
  'top-right': { angle: 45, icon: ArrowDown },
  left: { angle: 270, icon: ArrowDown },
  center: { angle: 0, icon: Corners },
  right: { angle: 90, icon: ArrowDown },
  'bottom-left': { angle: 225, icon: ArrowDown },
  bottom: { angle: 180, icon: ArrowDown },
  'bottom-right': { angle: 135, icon: ArrowDown },
  clockwise: { angle: 0, flip: true, icon: Reset },
  'counter-clockwise': { angle: 0, icon: Reset },
  vertical: { angle: 0, icon: ArrowUpDown },
  horizontal: { angle: 90, icon: ArrowUpDown },
};

const nineDirections: EffectNineDirections[] = [
  'top-left',
  'top',
  'top-right',
  'left',
  'center',
  'right',
  'bottom-left',
  'bottom',
  'bottom-right',
];

export const SliderNumeric: React.FC<CustomizeControlProps<SchemaNumber>> = ({
  name,
  params,
  divider,
  value,
  onChange,
  disabled,
}) => {
  const { translate, prefix } = useContext(TranslationContext);
  const [toDisplay, toData] = getDisplayTransformer(name);
  const [innerValue, setInnerValue] = React.useState<number>(value);
  const [isSliding, setIsSliding] = React.useState(false);

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  return (
    <>
      <Composites.SliderLabeled>
        <TextLabel
          shouldTranslate={false}
          value={translate(`label_${prefix}_${name.toLowerCase()}`)}
        />
        <Slider
          value={toDisplay(innerValue)}
          min={toDisplay(params.min || 0)}
          max={toDisplay(params.max || 100)}
          inputMin={toDisplay(params.min || 0)}
          inputMax={toDisplay(params.max || 100)}
          step={toDisplay(params.step || 1)}
          onChange={(newValue: number) => {
            const data = toData(newValue);
            setInnerValue(data);
            if (!isSliding && value !== data) {
              onChange(name, data);
            }
          }}
          onSlideStart={() => setIsSliding(true)}
          onSlideEnd={(newValue: number) => {
            setIsSliding(false);
            value !== toData(newValue) && onChange(name, toData(newValue));
          }}
          disabled={disabled}
        />
      </Composites.SliderLabeled>
      {divider && <Divider />}
    </>
  );
};

export const SliderNumericWithUnit: React.FC<
  CustomizeControlProps<SchemaNumberWithUnit>
> = ({ name, params, divider, value: { value, type }, onChange, disabled }) => {
  const [toDisplay, toData] = getDisplayTransformer(name);
  const { translate, prefix } = useContext(TranslationContext);
  const [innerValue, setInnerValue] = React.useState<number>(value);
  const [isSliding, setIsSliding] = React.useState(false);

  const showUnits = params.units!.length > 1;

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  return (
    <>
      <Composites.SliderLabeled>
        <TextLabel
          shouldTranslate={false}
          value={translate(`label_${prefix}_${name.toLowerCase()}`)}
        />
        <Slider
          hideNumericInput={showUnits}
          value={toDisplay(innerValue)}
          min={toDisplay(params.min || 0)}
          max={toDisplay(params.max || 100)}
          step={toDisplay(params.step || 1)}
          onChange={(newValue: number) => {
            const data = toData(newValue);
            setInnerValue(data);
            if (!isSliding && value !== data) {
              onChange(name, { value: toData(newValue), type });
            }
          }}
          onSlideStart={() => setIsSliding(true)}
          onSlideEnd={(newValue: number) => {
            setIsSliding(false);
            value !== toData(newValue) &&
              onChange(name, { value: toData(newValue), type });
          }}
          disabled={disabled}
        />

        {showUnits ? (
          <Composites.InputWithDropDown>
            <TextInput
              value={toDisplay(innerValue ?? value).toString(10)}
              inputHeight="small"
              type="text"
              shouldTranslate={false}
            />
            <DropDown
              value={type === 'percentage' ? '%' : type}
              shouldTranslate={false}
              options={prettyUnits(params.units as string[])}
              hasArrowIcon={false}
              onChange={(newType: string) =>
                onChange(name, {
                  value,
                  type:
                    newType === '%'
                      ? 'percentage'
                      : (newType as UnitLengthPercentage['type']),
                })
              }
              disabled={disabled}
            />
          </Composites.InputWithDropDown>
        ) : null}
      </Composites.SliderLabeled>
      {divider && <Divider />}
    </>
  );
};

export const AngleSlider: React.FC<CustomizeControlProps<SchemaNumber>> = ({
  name,
  divider,
  value,
  onChange,
  disabled,
}) => {
  const { translate, prefix } = useContext(TranslationContext);
  const [toDisplay, toData] = getDisplayTransformer(name);

  const [innerValue, setInnerValue] = React.useState<number>(value);
  const [isSliding, setIsSliding] = React.useState(false);

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  return (
    <>
      <Composites.AngleInputLabeled>
        <TextLabel
          shouldTranslate={false}
          value={translate(`label_${prefix}_${name.toLowerCase()}`)}
        />
        <AngleInput
          value={toDisplay(innerValue)}
          onChange={(newValue: number) => {
            const data = toData(newValue);
            setInnerValue(data);
            if (!isSliding && value !== data) {
              onChange(name, data);
            }
          }}
          onSlideStart={() => setIsSliding(true)}
          onSlideEnd={(newValue: number) => {
            setIsSliding(false);
            value !== toData(newValue) && onChange(name, toData(newValue));
          }}
          disabled={disabled}
        />
      </Composites.AngleInputLabeled>
      {divider && <Divider />}
    </>
  );
};

export const DropDownList: React.FC<CustomizeControlProps<SchemaString>> = ({
  name,
  params,
  divider,
  value,
  onChange,
  disabled,
}) => {
  const { translate, prefix } = useContext(TranslationContext);

  return params.enum ? (
    <>
      <Composites.DropDownLabeled>
        <TextLabel
          shouldTranslate={false}
          value={translate(`label_${prefix}_${name.toLowerCase()}`)}
        />
        <DropDown
          value={value}
          onChange={(newValue: string) => onChange(name, newValue)}
          disabled={disabled}
          shouldTranslate={false}
        >
          {params.enum!.map((item: string) => (
            <DropDownOption
              value={item}
              label={
                typeof item === 'number'
                  ? item
                  : translate(`enum_${item.toLowerCase()}`)
              }
              key={name + item}
              shouldTranslate={false}
            />
          ))}
        </DropDown>
      </Composites.DropDownLabeled>
      {divider && <Divider />}
    </>
  ) : null;
};

export const DirectionDropDownList: React.FC<
  CustomizeControlProps<SchemaString>
> = ({ name, params, divider, value, onChange, disabled }) => {
  const { translate, prefix } = useContext(TranslationContext);

  return params.enum ? (
    <>
      <Composites.DropDownLabeled>
        <TextLabel
          shouldTranslate={false}
          value={translate(`label_${prefix}_${name.toLowerCase()}`)}
        />
        <DropDown
          value={value}
          onChange={(newValue: string) => onChange(name, newValue)}
          disabled={disabled}
          shouldTranslate={false}
        >
          {params.enum!.map((item) => {
            // For scroll-out directional animations we need to show different language...
            const scrollOptionPrefix =
              prefix.includes('scroll') &&
              nineDirections.includes(item as EffectNineDirections)
                ? `${prefix}_`
                : '';
            // ...and rotate the icons
            const isScrollTo =
              prefix.includes('scroll_out') &&
              (nineDirections.includes(item as EffectNineDirections) ||
                typeof item === 'number');

            const {
              angle,
              icon: Icon,
              flip,
            } = directions[item as keyof typeof directions] ??
            (typeof item === 'number'
              ? {
                  angle: item,
                  icon: ArrowDown,
                  flip: false,
                }
              : {
                  angle: 0,
                  icon: () => null,
                  flip: false,
                });
            return (
              <DropDownIllustrationOption
                value={item}
                label={translate(
                  `enum_${scrollOptionPrefix}${item}`.toLowerCase(),
                )}
                key={name + item}
                shouldTranslate={false}
              >
                <span
                  style={
                    {
                      '--dropdown-direction-item-rotate':
                        angle + (isScrollTo ? 180 : 0),
                      '--dropdown-direction-item-flip': flip ? -1 : 1,
                    } as React.CSSProperties
                  }
                  className={styles.directionDropDownItem}
                >
                  <Icon />
                </span>
              </DropDownIllustrationOption>
            );
          })}
        </DropDown>
      </Composites.DropDownLabeled>
      {divider && <Divider />}
    </>
  ) : null;
};

export const ShapeThumbnails: React.FC<CustomizeControlProps<SchemaString>> = ({
  name,
  params,
  divider,
  value,
  onChange,
  disabled,
}) => {
  const { translate, prefix } = useContext(TranslationContext);
  const shapes = {
    circle: Shapes.Circle,
    rectangle: Shapes.Rectangle,
    diamond: Shapes.Rhombus,
    ellipse: Shapes.Ellipse,
  } as Record<Shape, () => React.JSX.Element>;

  const options = params.enum?.map((item: string) => {
    const Icon = shapes[item as Shape] || Shapes.Circle;
    return {
      value: item,
      // label: translate(`enum_${item.toLowerCase()}`),
      illustration: <Icon />,
    };
  });

  return params.enum ? (
    <>
      <Composites.Thumbnails>
        <TextLabel
          shouldTranslate={false}
          value={translate(`label_${prefix}_${name.toLowerCase()}`)}
        />
        <Thumbnails
          value={value}
          onChange={(newValue: string) => onChange(name, newValue)}
          options={options}
          maxThumbsPerRow={4}
          disabled={disabled}
        />
      </Composites.Thumbnails>
      {divider && <Divider />}
    </>
  ) : null;
};

export const BooleanSwitch: React.FC<CustomizeControlProps<SchemaBoolean>> = ({
  name,
  divider,
  value,
  onChange,
  disabled,
}) => {
  const { translate, prefix } = useContext(TranslationContext);

  return (
    <>
      <Composites.Checkboxes>
        <Checkbox
          label={translate(`label_${prefix}_${name.toLowerCase()}`)}
          value={value}
          onChange={(newValue: boolean) => onChange(name, newValue)}
          labelAfterSymbol
          disabled={disabled}
          type="T02"
          shouldTranslate={false}
        />
      </Composites.Checkboxes>
      {divider && <Divider />}
    </>
  );
};
